import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import './OfferRatePKO.scss';
import { useEffect, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import Modal from '@srm/ui/modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm } from 'react-hook-form';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IOption } from '@srm/ui/types/IOption';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferRatePKOFormData } from './IOfferRatePKOFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import SelectControl from '@srm/ui/controls/SelectControl';

export interface IOfferRatePKOProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferRatePKOFormData) => void;
    onClose?: () => void;
    offerUnAcceptableHint?: string;
}

const resultOptions: IOption[] = [
    { label: 'Допущен', value: 'Допущен' },
    { label: 'Не допущен', value: 'Не допущен' },
];

const OfferRatePKO = ({
    tableKey,
    isEdit,
    value,
    demandId,
    onSubmit,
    onClose,
    offerUnAcceptableHint,
}: IOfferRatePKOProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferRatePKOFormData>({
        mode: 'onBlur',
    });
    const hasUnsavedInput = useUnsavedFormInput(methods);

    const { isSubmitting, isDirty } = methods.formState;

    useEffect(() => {
        const defaultValue: IOfferRatePKOFormData = {
            result: value.result,
        };
        methods.reset(defaultValue);
    }, [value]);

    const onFormSubmit = async (form: IOfferRatePKOFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferRatePKO(value.key, demandId, tableKey, form)
            .then(() => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const errorLabel = error && (
        <div className="offer-rate-pko__error">
            <Hint role="alert" title={error} tone="danger" />
        </div>
    );

    const offerUnAcceptableLabel = value.acceptanceResult !== 1 && offerUnAcceptableHint && (
        <div className={'offer-crit-details__hint'}>
            <Hint role="alert" title={offerUnAcceptableHint} tone="warning" maxWidth="100%" />
        </div>
    );

    const modalFooter = (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-rate-pko-form"
                loading={isSubmitting}
                className={clsx('offer-rate-pko__button', 'offer-rate-pko__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal
            size="m"
            onClose={onClose}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            header={value.name}
            footer={isEdit ? modalFooter : null}
        >
            {errorLabel}
            {offerUnAcceptableLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-rate-pko-form"
                className="offer-rate-pko__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-rate-pko__content">
                        <div className="offer-rate-pko__row">
                            <SelectControl
                                label="Результат"
                                name="result"
                                readOnly={true}
                                disabled={!isEdit}
                                required={true}
                                rules={{ required: REQUIRED_MESSAGES.select }}
                                highlightMenuItems={false}
                                options={resultOptions}
                                control={methods.control}
                                setValue={(options) => {
                                    return options[0].value;
                                }}
                                getValue={(options: IOption[], value: any) => {
                                    return options.filter((x) => x.value === value);
                                }}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferRatePKO;
